/**
 * FlowSteps Component - 워크플로우 플로우 구성 (회색 패널 안 세로 단계)
 *
 * - 노드를 캔버스 흐름 순서로 정렬해 [아이콘] 이름 · 설명 으로 세로 나열, 단계 사이 연결선
 * - 워크플로우 템플릿 · 챗봇 미리보기 카드(.flow-card) 가운데 영역에서 사용
 * - 스타일: styles/flowCard.css
 *   <FlowSteps workflow={workflow} />
 */

import type { Workflow, WorkflowNode } from '../../api';
import FlowChatIcon from '../../assets/icons/flow/chat.svg?react';
import FlowScheduleIcon from '../../assets/icons/flow/schedule.svg?react';
import FlowManualRunIcon from '../../assets/icons/flow/manual-run.svg?react';
import FlowMailTriggerIcon from '../../assets/icons/flow/mail-trigger.svg?react';
import FlowMailIcon from '../../assets/icons/flow/mail.svg?react';
import FlowAiIcon from '../../assets/icons/flow/ai.svg?react';
import FlowDocSearchIcon from '../../assets/icons/flow/doc-search.svg?react';
import FlowConditionIcon from '../../assets/icons/flow/condition.svg?react';
import FlowReplyIcon from '../../assets/icons/flow/reply.svg?react';
import FlowWebSearchIcon from '../../assets/icons/flow/web-search.svg?react';
import FlowSummarizeIcon from '../../assets/icons/flow/summarize.svg?react';
import FlowMcpIcon from '../../assets/icons/flow/mcp.svg?react';
import FlowDriveIcon from '../../assets/icons/flow/google-drive.svg?react';
import FlowGmailIcon from '../../assets/icons/flow/gmail.svg?react';
import FlowSlackIcon from '../../assets/icons/flow/slack.svg?react';
import FlowNotionIcon from '../../assets/icons/flow/notion.svg?react';
import FlowNaverMailIcon from '../../assets/icons/flow/naver-mail.svg?react';
// NODE_STEPS 에 없는 노드 타입용 기본 아이콘
import FlowDefaultIcon from '../../assets/icons/common/grid.svg?react';
import '../../styles/flowCard.css';

type SvgIcon = React.FC<React.SVGProps<SVGSVGElement>>;

// 노드 타입 → 이름 · 선 아이콘 · 아이콘 색 (flowCard.css .flow-step-icon--{tone})
// ※ 워크플로우 빌더(WorkflowBuilderPage)도 이 매핑의 아이콘 · 색을 그대로 사용
export type FlowTone = 'green' | 'amber' | 'violet' | 'pink' | 'blue' | 'orange' | 'teal' | 'sky' | 'indigo' | 'plum' | 'slate';
export interface NodeStepInfo {
  label: string;
  icon: SvgIcon;
  tone: FlowTone;
}
export const NODE_STEPS: Record<string, NodeStepInfo> = {
  // 트리거
  'trigger':             { label: '채팅 시작',    icon: FlowChatIcon,        tone: 'green' },
  'schedule-trigger':    { label: '스케줄',       icon: FlowScheduleIcon,    tone: 'amber' },
  'mail-trigger':        { label: '메일 도착',    icon: FlowMailTriggerIcon, tone: 'violet' },
  'manual-trigger':      { label: '수동 실행',    icon: FlowManualRunIcon,   tone: 'sky' },
  // 액션
  'llm':                 { label: 'AI 응답',      icon: FlowAiIcon,          tone: 'pink' },
  'knowledge-retrieval': { label: '문서 검색',    icon: FlowDocSearchIcon,   tone: 'blue' },
  'condition':           { label: '판단',         icon: FlowConditionIcon,   tone: 'orange' },
  'output':              { label: '답변 전송',    icon: FlowReplyIcon,       tone: 'teal' },
  'web-search':          { label: '웹 검색',      icon: FlowWebSearchIcon,   tone: 'sky' },
  'summarize':           { label: '요약',         icon: FlowSummarizeIcon,   tone: 'indigo' },
  // 커넥터 (메일 = 보라, 그 외 서비스 대표색에 가깝게)
  'google-drive':        { label: 'Google Drive', icon: FlowDriveIcon,       tone: 'blue' },
  'gmail':               { label: 'Gmail',        icon: FlowGmailIcon,       tone: 'violet' },
  'slack':               { label: 'Slack',        icon: FlowSlackIcon,       tone: 'plum' },
  'notion':              { label: 'Notion',       icon: FlowNotionIcon,      tone: 'slate' },
  'naver':               { label: '네이버 메일',  icon: FlowNaverMailIcon,   tone: 'green' },
  'email':               { label: '이메일 커넥터', icon: FlowMailIcon,        tone: 'violet' },
  'mcp':                 { label: 'MCP 서버',     icon: FlowMcpIcon,         tone: 'slate' },
};

// 커넥터 동작 → 단계 설명
const OPERATION_LABELS: Record<string, string> = {
  send: '메일 발송', read: '읽기', search: '검색', reply: '답장', label: '라벨 지정', react: '반응',
  watch_new: '새 파일 감지', watch_modified: '수정 감지', upload: '업로드', list: '목록',
  create_page: '페이지 생성', add_db_item: 'DB 항목 추가', watch_db: 'DB 감지', watch_page: '페이지 감지',
};

const WEEKDAY_LABELS: Record<string, string> = {
  monday: '월', tuesday: '화', wednesday: '수', thursday: '목', friday: '금', saturday: '토', sunday: '일',
};

const pad2 = (n: unknown, fallback: number) => String(typeof n === 'number' ? n : fallback).padStart(2, '0');

// 단계 이름 옆 설명 (스케줄 주기 · 커넥터 동작 · 고정 질문) - 알 수 없으면 생략
const describeNode = (node: WorkflowNode): string | undefined => {
  const d = node.data ?? {};
  if (node.type === 'schedule-trigger') {
    switch (d.scheduleType ?? 'daily') {
      case 'daily':    return `매일 ${pad2(d.dailyHour, 9)}:${pad2(d.dailyMinute, 0)}`;
      case 'weekly':   return `매주 ${WEEKDAY_LABELS[String(d.weeklyDay ?? 'monday')] ?? ''}요일 ${pad2(d.weeklyHour, 9)}:${pad2(d.weeklyMinute, 0)}`;
      case 'hourly':   return '매시간';
      case 'interval': return '일정 간격';
      default:         return undefined;
    }
  }
  if (node.type === 'knowledge-retrieval' && typeof d.fixedQuery === 'string' && d.fixedQuery) return d.fixedQuery;
  if (typeof d.operation === 'string') return OPERATION_LABELS[d.operation];
  return undefined;
};

// 캔버스 플로우 순서대로 정렬 (nodes 배열은 추가한 순서라 실제 흐름과 다름)
// - 연결선(edges) 방향을 따르고, 순서가 정해지지 않는 노드끼리는 캔버스 왼쪽 → 오른쪽 (같으면 위 → 아래)
export const sortByFlow = (nodes: WorkflowNode[], edges: Workflow['edges'] = []): WorkflowNode[] => {
  const byPosition = (a: WorkflowNode, b: WorkflowNode) =>
    (a.position?.x ?? 0) - (b.position?.x ?? 0) || (a.position?.y ?? 0) - (b.position?.y ?? 0);
  const ids = new Set(nodes.map((n) => n.id));
  const inDegree = new Map(nodes.map((n) => [n.id, 0]));
  const next = new Map<string, string[]>();
  edges.forEach((e) => {
    if (!ids.has(e.source) || !ids.has(e.target)) return;
    inDegree.set(e.target, (inDegree.get(e.target) ?? 0) + 1);
    next.set(e.source, [...(next.get(e.source) ?? []), e.target]);
  });

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const ready = nodes.filter((n) => inDegree.get(n.id) === 0);
  const sorted: WorkflowNode[] = [];
  while (ready.length > 0) {
    ready.sort(byPosition);
    const node = ready.shift()!;
    sorted.push(node);
    (next.get(node.id) ?? []).forEach((id) => {
      const left = (inDegree.get(id) ?? 0) - 1;
      inDegree.set(id, left);
      if (left === 0) ready.push(nodeById.get(id)!);
    });
  }
  // 순환 연결이 있으면 남은 노드는 위치 순으로 뒤에
  const rest = nodes.filter((n) => !sorted.includes(n)).sort(byPosition);
  return [...sorted, ...rest];
};

const MAX_STEPS = 5;

export function FlowSteps({ workflow }: { workflow: Workflow }) {
  const nodes = sortByFlow(workflow.nodes ?? [], workflow.edges);

  return (
    <div className="flow-panel">
      <div className="flow-panel-title">플로우 구성 · {nodes.length}단계</div>
      <ol className="flow-steps">
        {nodes.slice(0, MAX_STEPS).map((node) => {
          const step = NODE_STEPS[node.type];
          const desc = describeNode(node);
          return (
            <li key={node.id} className="flow-step">
              <span className={`flow-step-icon flow-step-icon--${step?.tone ?? 'slate'}`}>
                {step ? <step.icon /> : <FlowDefaultIcon />}
              </span>
              <span className="flow-step-text">
                <b>{step?.label ?? node.type}</b>
                {desc && <span> · {desc}</span>}
              </span>
            </li>
          );
        })}
      </ol>
      {nodes.length > MAX_STEPS && (
        <span className="flow-more">+{nodes.length - MAX_STEPS}단계 더</span>
      )}
    </div>
  );
}

export default FlowSteps;
